import { FC, useState } from 'react';

import { Alert, CircularProgress, Stack } from '@mui/material';

import CreateNewMetadataDialog from '@/shared/components/dialogs/CreateNewMetadataDialog';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import MetadataFormAccordion from '@/shared/components/forms/MetadataField/components/MetadataFormAccordion';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import DialogName from '@/shared/types/dialogName.enum';

import ProductVariantsForm from '../components/ProductVariantsForm';
import InventoryTable from './components/InventoryTable/InventoryTable';
import Title from './components/Title';
import UpdateProductDialog from './components/UpdateProductDialog';
import UpdateProductVariantsDialog from './components/UpdateProductVariantsDialog';
import useFetchProductAndMetadataForEdit from './hooks/useFetchProductAndMetadataForEdit';
import useProductOperations from './hooks/useProductOperations';

const ProductEdit: FC = () => {
  const { product, metadata, productId, isPageLoading, apiError } =
    useFetchProductAndMetadataForEdit();

  const {
    dialog,
    progressDialog,
    notificationBar,
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
    onCreateNewMetadata,
    onProductUpdate,
    onProductVariantsUpdate,
    onProductVariantsCreate,
  } = useProductOperations({ productId });

  // Todo: remove old functionality provided above and recheck product creation
  const [products, setProducts] = useState<{
    product: Record<string, unknown>;
    details: Record<string, unknown>;
    variants: {
      variant_id: string;
      isNotRemovable?: boolean | undefined;
      required: Record<string, unknown>;
      optional: Record<string, unknown>;
    }[];
  }>({
    product: {},
    details: {},
    variants: [],
  });

  return (
    <Stack>
      <Title
        productId={productId}
        onUpdateProduct={() => {
          if (
            Object.keys(products.product).length > 0 ||
            Object.keys(products.details).length > 0
          ) {
            void onProductUpdate({
              ...products.product,
              ...products.details,
            });
          }

          const variantsToUpdate = products.variants.filter(
            (item) => item.isNotRemovable === true,
          );

          const variantsToCreate = products.variants.filter(
            (item) => item.isNotRemovable === false,
          );

          if (variantsToUpdate.length > 0) {
            void onProductVariantsUpdate(
              variantsToUpdate.map((item) => ({
                variant_id: item.variant_id,
                ...item.required,
                ...item.optional,
              })),
            );
          }

          if (variantsToCreate.length > 0) {
            void onProductVariantsCreate(
              variantsToCreate.map((item) => ({
                ...item.required,
                ...item.optional,
              })),
            );
          }
        }}
        onOpenCreateMetadataDialog={() =>
          onUpdateDialogState(DialogName.CreateMetadata, {
            open: true,
            errors: [],
          })
        }
      />

      {apiError && <Alert severity='error'>{apiError}</Alert>}

      {isPageLoading && (
        <Stack alignItems='center' justifyContent='center' height='70vh'>
          <CircularProgress />
        </Stack>
      )}

      {!isPageLoading && (
        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            alignItems: 'start',
            gap: 2,
          }}
        >
          {product && metadata && (
            <>
              <MetadataFormAccordion
                accordionProps={{ title: 'Product' }}
                metadata={metadata}
                initialData={product}
                levelConstraint='PRODUCT'
                fields={{
                  required: products.product,
                  optional: products.details,
                }}
                getValues={(values) => {
                  setProducts((prev) => ({
                    ...prev,
                    product: { ...prev.product, ...values.required },
                    details: { ...prev.details, ...values.optional },
                  }));
                }}
              />
              <ProductVariantsForm
                metadata={metadata}
                initialData={product.variants}
                getVariants={(values) => {
                  setProducts((prev) => ({ ...prev, variants: values }));
                }}
              />
            </>
          )}

          <Stack sx={{ gridColumn: '1/-1' }}>
            <InventoryTable product={product} />
          </Stack>
        </Stack>
      )}

      {dialog?.updateProductVariants?.open && (
        <UpdateProductVariantsDialog
          errors={dialog.updateProductVariants.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.UpdateProductVariants, {
              open: false,
            })
          }
        />
      )}

      {dialog?.updateProduct?.open && (
        <UpdateProductDialog
          errors={dialog.updateProduct.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.UpdateProduct, {
              open: false,
            })
          }
        />
      )}

      {dialog?.createMetadata?.open && (
        <CreateNewMetadataDialog
          metadata={metadata}
          module='products'
          errors={dialog.createMetadata.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.CreateMetadata, {
              open: false,
            })
          }
          proceed={(formToSave) => {
            void onCreateNewMetadata(formToSave);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </Stack>
  );
};

export default ProductEdit;
